import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import { forceFileDownload, getTokenHeader } from "@/helpers/commonHelper";
import EventListTable from "@/components/Events/EventListTable/EventListTable.vue";
import EventPreview from "@/components/Events/EventPreview/EventPreview.vue";
import EventFilter from "@/components/Events/EventFilter/EventFilter.vue";
import EventEditDialog from "@/components/Events/EventEditDialog/EventEditDialog.vue";
import EventExportPopup from "@/components/Events/EventExportPopup/EventExportPopup.vue";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
import axios from "axios";
import _, { isEqual } from "lodash";
import { uploadFile } from "@/helpers/fileHelper";
import {
  XML_HEADER_ENTITIES,
  getXMLArrayForEvent,
  SELECTABLE_EXPORT_FIELDS,
  stringifyUTF8,
  getIsAllDay
} from "@/helpers/eventHelper";
import moment from "moment";
import XLSX from "xlsx";
import jsontoxml from "jsontoxml";
import { DateTime } from "luxon";

const defaultEvent = {
  type: "basic",
  video_urls: [],
  highlight: {
    standard: false,
    own: false
  },
  features: [],
  license: {},
  additional_data: [{}],
  all_day: false,
  start_time: null,
  change_series: false,
  pictures: [],
  files: [],
  instructions: "",
  discount: [],
  location: null,
  location_text: "",
  operator: "",
  channels: [],
  category: null,
  additional_categories: [],
  duration: 0,
  newMoments: [],
  updateMoments: [],
  deleteMoments: [],
  showFirst: "pictures",
  active: true,
  ai_attribution: ""
};
const defaultFilter = {
  limit: 10,
  start: moment()
    .startOf("day")
    .toISOString(),
  sort: "start",
  sortBy: ["start"],
  page: 1,
  itemsPerPage: 10,
  sortDesc: [false],
  groupBy: ["groupStart"],
  groupDesc: [false],
  mustSort: false,
  multiSort: false,
  direction: "asc",
  query: {},
  isDefault: true,
  distinct: false
};

export default {
  name: "EventList",
  props: ["editId"],
  async mounted() {
    const itemsPerPage = defaultFilter.itemsPerPage;
    this.filter.itemsPerPage = this.filter.limit = itemsPerPage;
    await this.checkForEventToEdit();
    if (this.selectedPrincipal?.id) {
      await this.fetchData();
    }
  },
  data: () => ({
    overlay: false,
    dialogKey: 0,
    visibleColumnKeys: [
      "image",
      "preview",
      "title",
      "type",
      "startTime",
      "organizer",
      "locationTitle",
      "locationCity",
      "regionality",
      "channels",
      "highlight.standard",
      "highlight.own",
      "actions"
    ],
    regionalities: [
      { title: "regional", value: "communal" },
      { title: "lokal", value: "local" },
      { title: "überregional", value: "regional" },
      { title: "intern", value: "internal" }
    ],
    filter: {
      ...Object.assign({}, { ...defaultFilter })
    },
    previewedEvent: {},
    editedEvent: _.cloneDeep(Object.assign({}, { ...defaultEvent })),
    showPreviewDialog: false,
    showEditDialog: false,
    saving: false,
    loadingExport: false,
    exportSortOptions: {
      primary: undefined,
      secondary: undefined,
      third: undefined,
      fourth: undefined,
      fifth: undefined
    },
    exportType: false,
    isLoading: true,
    exportPopupIsOpen: false,
    selectedExportFields: [],
    currentPage: 1,
    destroyList: [],
    editedEventMomentsFetching: false,
    editedEventFetching: false,
    editedEventFetchingDelayed: false
  }),
  methods: {
    async checkForEventToEdit() {
      if (this.editId) {
        const fullItem = await this.getFullEventFromListItem({
          _id: this.editId
        });
        if (fullItem?._id) await this.editEvent(fullItem);
      }
    },
    async getFullEventFromListItem(item) {
      const headers = getTokenHeader();
      const principals = [
        this.$store.state.selectedPrincipal?.id,
        ...this.$store.state.selectedPrincipal?.subPrincipals?.map(
          subPrincipal => subPrincipal.id
        )
      ];

      this.editedEventMomentsFetching = true;
      this.$store
        .dispatch("getMomentsForEvent", item._id)
        .then(() => (this.editedEventMomentsFetching = false));

      const response = await axios.get(
        `${env.events_api_client_url}event/${item._id}`,
        {
          params: {
            principal_id: { $in: principals }
          },
          headers
        }
      );
      return response.data;
    },

    async editEvent(item) {
      this.addChannelDefaultFields(defaultEvent);
      let eventToEdit = {};
      let eventToEditPromise;
      this.$store.state.momentsForEvent = [];
      if (item) {
        if (item._id) {
          this.editedEventFetching = true;
          eventToEditPromise = this.getFullEventFromListItem(item);

          // Only show loading overlay if fetch takes longer than 1000ms
          const loadingTimeout = setTimeout(() => {
            this.editedEventFetchingDelayed = true;
          }, 1000);

          eventToEditPromise.finally(() => {
            clearTimeout(loadingTimeout);
            this.editedEventFetching = false;
            this.editedEventFetchingDelayed = false;
          });
        }
        this.editedEvent = _.assign(
          {},
          _.cloneDeep({ ...defaultEvent }),
          _.cloneDeep({ ...item })
        );
      } else {
        this.editedEvent = _.cloneDeep({
          ...defaultEvent,
          channels: this.config.default_channels
        });
      }
      this.dialogKey++;
      this.toggleEditDialog(true);

      if (!eventToEditPromise) {
        return;
      }
      eventToEdit = await eventToEditPromise;
      if (eventToEdit?._id !== item?._id) {
        return;
      }

      this.editedEvent = _.assign(
        {},
        _.cloneDeep({ ...defaultEvent }),
        _.cloneDeep({ ...item }),
        _.cloneDeep({ ...eventToEdit })
      );
      this.editedEvent.start = item.start;
    },
    addChannelDefaultFields(defaultEvent) {
      defaultEvent.alternative_content = {};
      this.config?.standard_channels?.forEach(c => {
        defaultEvent.alternative_content[c.value] = {
          description: "",
          short_description: ""
        };
      });
    },
    async previewEvent(item) {
      this.previewedEvent = await this.getFullEventFromListItem(item);
      this.showPreviewDialog = true;
    },
    closePreview() {
      this.previewedEvent = null;
      this.showPreviewDialog = false;
    },
    getTypeIcon(item) {
      switch (item.type) {
        case "series":
          return "mdi-calendar-range";
        default:
          return "mdi-calendar";
      }
    },
    formatDate(date) {
      return moment(date).format("DD.MM.YYYY HH:mm");
    },
    getStartEndDate(item) {
      const { start, end } = item ?? {};
      if (start) {
        if (end) return `${this.formatDate(start)} - ${this.formatDate(end)}`;
        else return this.formatDate(start);
      }
    },
    getStartTime(item) {
      if (getIsAllDay(item)) {
        return "Ganztägig";
      }
      return moment(item.moment?.from ?? item.start).format("HH:mm");
    },
    getOrganizer(item) {
      const { operator } = item ?? {};
      if (operator && this.organizerById?.[operator])
        return this.organizerById[operator];
    },
    getRegionality(item) {
      const regionality = this.regionalities.find(r => item.includes(r.value));
      return regionality.title;
    },

    getLocation(item, withLocationText = true) {
      const { location } = item ?? {};
      let returnValue = "";
      if (location && this.locationsById?.[location])
        returnValue += this.locationsById[location];
      if (item.location_text && withLocationText)
        returnValue += ` ${item.location_text}`;
      return returnValue;
    },
    getLocationCity(item) {
      return (
        item?.locationObj?.[0]?.address?.city ??
        this.locations?.find(l => l._id === item.location)?.address?.city ??
        ""
      );
    },
    toggleEditDialog(value) {
      if (!value) {
        this.editedEvent = _.cloneDeep({ ...defaultEvent });
        this.$refs.editDialog?.$refs?.processingStatus?.$refs?.logOverview?.resetLogs();
        this.$refs.editDialog?.$refs?.eventEditForm?.reset();
      }
      this.showEditDialog = value;
    },
    async fetchData() {
      this.isLoading = true;
      try {
        await this.$store.dispatch("getEventsConfig");
        await this.$store.dispatch("getFeatureGroupsForEvents");
        await this.$store.dispatch("getCategoryGroupsForEvents");
        await this.$store.dispatch("getOrganizer", {
          eventList: true
        });
        await this.$store.dispatch("getAllUsers");
        await this.$store.dispatch("getThirdPartySystems", {
          module: "Events"
        });
        const itemToEdit = this.events.find(
          e => e._id === this.$route.params.editId
        );
        if (itemToEdit) this.editEvent(itemToEdit);
        this.isLoading = false;
      } catch (error) {
        await this.$store.dispatch("createError", error);
      }
    },
    async deleteEvent(item) {
      const message =
        item.type === "series"
          ? "Achtung die ganze Serie wird gelöscht. Sind Sie sicher, dass Sie fortfahren möchten?"
          : "Sind Sie sicher, dass Sie diese Veranstaltung löschen möchten?";
      const isConfirmed = await this.$confirm(message, confirmWarningConfig);
      if (isConfirmed) {
        try {
          const response = await this.deleteEventRequest(item._id);
          if (response.status === 200) {
            await this.$store.dispatch("getEvents", this.filter);
            this.$refs.eventListTable?.clearSelection?.();
            await this.$store.dispatch(
              "createSuccess",
              "Veranstaltung wurde erfolgreich gelöscht!"
            );
          }
        } catch (error) {
          await this.$store.dispatch("createError", error);
        }
      }
    },
    async deleteEventRequest(eventId) {
      const headers = getTokenHeader();
      return axios.delete(`${env.events_api_client_url}event/${eventId}`, {
        params: { principal_id: this.selectedPrincipal.id },
        headers
      });
    },
    async copyEvent(item) {
      const isConfirmed = await this.$confirm(
        "Sind Sie sicher, dass Sie diese Veranstaltung kopieren möchten?",
        confirmWarningConfig
      );
      if (isConfirmed) {
        if (!item) item = this.editedEvent;
        const headers = getTokenHeader();
        try {
          const response = await axios.post(
            `${env.events_api_client_url}event/copy/${item._id}`,
            { item },
            { headers }
          );
          if (response.status === 201)
            await this.$store.dispatch(
              "createSuccess",
              "Veranstaltung kopiert!"
            );
          else await this.$store.dispatch("createError");
          await this.$store.dispatch("getEvents", this.filter);
        } catch (e) {
          console.error(e);
          await this.$store.dispatch("createError", e);
        }
      }
    },
    async updateCheckboxState({ item, prop }) {
      const message =
        item.type === "series"
          ? "Achtung diese Änderung bezieht sich auf die ganze Serie. Sind Sie sicher, dass Sie fortfahren möchten?"
          : "Sind Sie sicher, dass Sie diese Änderung durchführen möchten?";
      const isConfirmed = await this.$confirm(message, confirmWarningConfig);
      if (isConfirmed) {
        if (prop.includes(".")) {
          const { 0: obj, 1: field } = prop.split(".");
          item[obj] = { ...item[obj], [field]: !item[obj][field] };
        } else {
          item[prop] = !item[prop];
        }
        await this.updateEvent(item);
      }
    },
    async updateCheckboxStateMoment({ item, prop }) {
      const message =
        "Sind Sie sicher, dass Sie diese Änderung durchführen möchten?";
      const isConfirmed = await this.$confirm(message, confirmWarningConfig);
      const eventId = item._id;
      const moment = item.moment;
      if (isConfirmed) {
        item.moment[prop] = !item.moment[prop];
        this.$store.dispatch("updateMoment", {
          moment,
          eventId
        });
        await this.updateEvent(item);
      }
    },
    async saveChannels(item) {
      if (!item.deleted) {
        await this.updateEvent(item);
      }
    },
    async createEvent(
      event,
      successMessage = "Veranstaltung wurde erfolgreich angelegt!"
    ) {
      if (!event) event = this.editedEvent;
      if (event?.newMoments?.length > 1) event.type = "series";
      const headers = getTokenHeader();
      try {
        const response = await axios.post(
          `${env.events_api_client_url}event`,
          event,
          {
            headers
          }
        );
        if (response.status === 201 || response.status === 200) {
          await this.$store.dispatch("getEvents", this.filter);
          await this.$store.dispatch("getMomentsForEvent", response.data._id);
          await this.$store.dispatch("createSuccess", successMessage);
          return response.data;
        }
      } catch (error) {
        if (error?.response?.data?.message === "Unexpected series")
          await this.$store.dispatch("createError", {
            text: "Serie fehlerhaft!",
            error
          });
        else await this.$store.dispatch("createError", error);
      }
    },
    async updateEvent(event) {
      if (!event) event = this.editedEvent;
      const transformToSeries =
        (event.newMoments?.length || 0) - (event.deleteMoments?.length || 0);
      if (transformToSeries) event.type = "series";

      const headers = getTokenHeader();
      try {
        const response = await axios.put(
          `${env.events_api_client_url}event`,
          event,
          { params: { principal_id: this.selectedPrincipal.id }, headers }
        );
        if (response.status === 201 || response.status === 200) {
          await this.$store.dispatch("getEvents", {
            ...this.filter,
            page: this.currentPage
          });
          await this.$store.dispatch("getMomentsForEvent", event._id);
          await this.$store.dispatch(
            "createSuccess",
            "Veranstaltung wurde erfolgreich aktualisiert!"
          );
          return response.data;
        }
      } catch (error) {
        if (error?.response?.data?.message === "Unexpected series")
          await this.$store.dispatch("createError", {
            text: "Serie fehlerhaft!",
            error
          });
        else await this.$store.dispatch("createError", error);
      }
    },
    setFilterPage(page) {
      this.filter.page = page;
    },
    setFilterLimit(limit) {
      this.filter.limit = limit;
      this.filter.itemsPerPage = limit;
      localStorage.setItem("itemsPerPage", limit);
    },
    async applyFilter(filter) {
      if (!filter) filter = this.filter;
      if (!isEqual(filter, this.filter)) this.filter.page = filter.page || 1;
      this.currentPage = this.filter.page;
      this.isLoading = true;
      await this.$store.dispatch("getEvents", filter);
      this.isLoading = false;
    },
    async resetFilter() {
      this.filter = { ...Object.assign({}, { ...defaultFilter }) };
      await this.$store.dispatch("getEvents", this.filter);
    },
    applySearchText: _.debounce(async function() {
      if (this.filter.s.length >= 3 || !this.filter.s.length)
        await this.applyFilter(this.filter);
    }, 500),
    async cancelEdit() {
      this.editedEvent = _.cloneDeep({ ...defaultEvent });
      this.toggleEditDialog(false);
      if (this.editId) window.location.href = "/events/events";
    },
    async save(closeDialog) {
      if (!this.saving) {
        this.saving = true;
        if (this.editedEvent?.start) {
          if (!this.editedEvent?._id && this.editedEvent.duration) {
            this.editedEvent.end = DateTime.fromISO(this.editedEvent.start)
              .plus({ minutes: this.editedEvent.duration || 0 })
              .toJSDate();
          }
          this.editedEvent = this.cleanUp(this.editedEvent);
          this.editedEvent.newMoments = this.$store.state.momentsForEvent.filter(
            d => !d._id
          );
          this.editedEvent.updateMoments = this.$store.state.momentsForEvent.filter(
            d => d._id && d.updated
          );
          if (this.editedEvent.pictures?.length) {
            await this.editedEvent.pictures.asyncForEach(async pic => {
              if (pic && pic instanceof File) {
                const { url } = await this.uploadFile(pic, undefined, "events");
                pic.url = url;
                pic.principal_id = this.selectedPrincipal.id;
                pic.originalUrl = url;
                pic.dataUrl = "";
              }
            });
          }

          if (this.editedEvent.files?.length) {
            await this.editedEvent.files.asyncForEach(async file => {
              if (file && file instanceof File) {
                const { url } = await this.uploadFile(
                  file,
                  undefined,
                  "events"
                );
                file.url = url;
                file.principal_id = this.selectedPrincipal.id;
                file.originalUrl = url;
                file.dataUrl = "";
              }
            });
          }
          let success = false;
          if (!this.editedEvent._id) {
            this.editedEvent.principal_id = this.selectedPrincipal.id;
            const savedEvent = await this.createEvent(this.editedEvent);
            if (savedEvent) {
              this.editedEvent._id = savedEvent._id;
              success = true;
            }
          } else {
            success = await this.updateEvent(this.editedEvent);
          }
          if (success) {
            await this.removeFilesOnSave();
          }

          const logOverviewRef = this.$refs.editDialog?.$refs?.processingStatus
            ?.$refs?.logOverview;
          const logsFetched = logOverviewRef?.logsFetched;
          logOverviewRef?.resetLogs();

          if (closeDialog && success) {
            this.editedEvent = _.cloneDeep({ ...defaultEvent });
            this.toggleEditDialog(!closeDialog);
            if (this.editId) window.location.href = "/events/events";
          } else if (logOverviewRef && logsFetched) {
            await logOverviewRef.getLogs();
          }
        } else {
          await this.$store.dispatch("createError", {
            text: "Bitte geben Sie einen Termin für die Veranstaltung an!"
          });
          if (this.editId) window.location.href = "/events/events";
        }
        this.saving = false;
      }
    },
    async removeFilesOnSave() {
      await this.destroyList.asyncForEach(async id => {
        try {
          const headers = getTokenHeader();
          await axios.delete(
            `${env.events_api_client_url}uploads/${id}/${this.editedEvent._id}`,
            { headers }
          );
        } catch (err) {
          await this.$store.dispatch("createError", {
            ...err,
            text: "Fehler beim Dateien entfernen"
          });
        }
      });
      this.destroyList = [];
    },
    cleanUp(event) {
      if (event) {
        if (!event.operator) event.operator = null;
        event.additional_data = event.additional_data.filter(
          data => !!data.type_id
        );
        return event;
      }
    },
    uploadFile,
    async exportList() {
      this.loadingExport = true;

      if (this.exportType === "json") this.exportJson();
      else if (this.exportType === "xml") this.exportXml();
      else if (this.exportType === "image") await this.exportImages();
      else this.exportTable(this.exportType);
      this.loadingExport = false;
      this.exportPopupIsOpen = false;
    },
    async exportXml() {
      const data = await this.getExportEvents();
      const xmlFile = jsontoxml(
        {
          Veranstaltungen: this.formatJsonForXml(data)
        },
        {
          xmlHeader: '<?xml version="1.0" encoding="UTF-8"?>\n',
          docType: `root [ ${XML_HEADER_ENTITIES} ]`,
          prettyPrint: true,
          escape: true
        }
      );
      forceFileDownload(xmlFile, "veranstaltungen.xml");
    },
    async exportJson() {
      const data = await this.getExportEvents();
      forceFileDownload(JSON.stringify(data), "veranstaltungen.json");
    },
    async exportImages() {
      let poisToExportImages = [];
      const data = await this.getExportEvents();
      data.forEach(event => poisToExportImages.push(event._id));
      poisToExportImages = _.uniq(poisToExportImages);
      const headers = getTokenHeader();
      try {
        const { data } = await axios.post(
          `${env.events_api_client_url}event/exportImagesAsZip`,
          poisToExportImages,
          {
            headers,
            responseType: "arraybuffer"
          }
        );
        forceFileDownload(data, "veranstaltungsBilder.zip");
      } catch (error) {
        await this.$store.dispatch("createError", error);
      }
    },
    async getExportEvents() {
      let data = [];
      try {
        const headers = getTokenHeader();
        for (let page = 1; data.length < this.eventCount; page++) {
          this.filter.limit = 200;
          this.filter.page = page;
          this.filter.getAllFields = true;
          const res = await axios.post(
            `${env.events_api_client_url}event/for_calendar`,
            this.filter,
            { headers }
          );
          await res?.data?.results.asyncForEach(async event => {
            if (event.type === "series") {
              await this.$store.dispatch("getMomentsForEvent", event._id);
              event.moments = this.$store.state.momentsForEvent;
            }
            data.push(event);
          });
        }
        this.filter.limit = defaultFilter.itemsPerPage;
        this.filter.page = 1;
        await this.fetchData();
      } catch (error) {
        await this.$store.dispatch("createError", error);
      }

      const sortBy = Object.values(this.exportSortOptions)
        .filter(sortOption => !!sortOption)
        .map(sortOption => sortOption.sortBy);
      return _.sortBy(data, sortBy);
    },
    async exportTable() {
      this.overlay = true;
      const table = [this.selectedExportFields.map(field => field)];
      const children = _.flatten(
        SELECTABLE_EXPORT_FIELDS.map(field => field.children)
      );

      const exportFieldGetter = this.selectedExportFields.map(
        fieldId => children.find(field => field.id === fieldId).getValue
      );

      const events = await this.getExportEvents();
      events.forEach(event => {
        table.push(
          exportFieldGetter.map(getter => stringifyUTF8(getter(event)))
        );
      });
      // create XLSX file structure
      const ws = XLSX.utils.aoa_to_sheet(table);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, ws, "Veranstaltungen");
      XLSX.writeFile(workbook, `veranstaltungen.${this.exportType}`);
      this.overlay = false;
    },

    formatJsonForXml(events) {
      const returnEvents = events.map(event => {
        this.selectedExportFields = [...new Set(this.selectedExportFields)];
        return {
          Veranstaltung: getXMLArrayForEvent(event, this.selectedExportFields)
        };
      });

      returnEvents.map(event => {
        event.Veranstaltung.map(field => {
          field.name = field.name.replace(/ /g, "_"); // ensure all XML tags are without spaces
          field.text = field?.text?.trim();
          return field;
        });
        return event;
      });

      return returnEvents;
    },
    async openPopup(exportType) {
      this.exportType = exportType;
      if (this.exportType === "json" || this.exportType === "image")
        await this.exportList();
      else this.exportPopupIsOpen = true;
    }
  },
  computed: {
    currentHeaders() {
      const selected = new Set(this.visibleColumnKeys);
      return this.headers.filter(header => selected.has(header.value));
    },
    columnOptions() {
      return this.headers.map(header => ({
        text: header.text || header.value,
        value: header.value
      }));
    },
    columnSettingsStorageKey() {
      return "events_columns";
    },
    headers() {
      const headers = [
        {
          text: "Vorschaubild",
          align: "start",
          value: "image",
          width: "10%",
          sortable: false
        },
        {
          text: "Vorschau",
          value: "preview",
          align: "start",
          width: "6%",
          sortable: false
        },
        { text: "Titel", value: "title" },
        { text: "Typ", value: "type" },
        { text: "Start Uhrzeit", value: "startTime" },
        { text: "Veranstalter", value: "organizer" },
        { text: "Standort (Titel)", value: "locationTitle" },
        { text: "Standort (Ort)", value: "locationCity" },
        { text: "Bezug", value: "regionality" }
      ];
      // hide channels-column if empty or only web-channel
      if (
        this.config?.channels?.length &&
        this.config.channels.join("") !== "web"
      ) {
        headers.push({ text: "Kanäle", value: "channels" });
      }
      if (this.config.useHighlightFrom === "standard") {
        headers.push({ text: "Top-Event", value: "highlight.standard" });
      } else if (this.config.useHighlightFrom === "own") {
        if (this.selectedPrincipal.subPrincipals?.length) {
          // Non-Breaking Hyphen in text "Top‑Event" for better UI.
          headers.push({
            text: `Top‑Event (${this.selectedPrincipal.name})`,
            value: "highlight.own"
          });
          headers.push({
            text: "Top‑Event (Eigentümer)",
            value: "highlight.standard"
          });
        } else {
          headers.push({ text: "Top-Event", value: "highlight.own" });
        }
      }
      headers.push({ text: "Aktionen", value: "actions", sortable: false });
      return headers;
    },
    config() {
      return this.$store.state.eventConfig;
    },
    events() {
      return this.$store.state.events;
    },
    eventCount() {
      return Number(this.$store.state.eventCount ?? 0);
    },
    organizer() {
      return this.$store.state.organizer;
    },
    organizerById() {
      return this.$store.state.organizerById;
    },
    locations() {
      return this.$store.state.locations;
    },
    locationsById() {
      return this.$store.state.locationsById;
    },
    channels() {
      const ownChannels = this.$store.state.eventConfig.own_channels ?? [];
      const standardChannels =
        this.$store.state.eventConfig?.standard_channels ?? [];
      const activeOwnChannels = ownChannels.filter(channel =>
        this.$store.state.eventConfig?.channels?.includes(channel.value)
      );
      const activeStandardChannels = standardChannels.filter(channel =>
        this.$store.state.eventConfig?.channels?.includes(channel.value)
      );
      return activeOwnChannels.concat(activeStandardChannels);
    },
    selectedPrincipal() {
      return this.$store.state.selectedPrincipal;
    },
    users() {
      return this.$store.state.users;
    },
    categoryGroups() {
      return this.$store.state.categoryGroupsForEvents;
    },
    categories() {
      return _.flatten(this.categoryGroups?.map(cg => cg.categories)) ?? [];
    },
    features() {
      return (
        _.flatten(
          this.$store.state.featureGroupsForEvents?.map(fg => fg.features)
        ) ?? []
      );
    },
    featureGroups() {
      return this.$store.state.featureGroupsForEvents;
    },
    groupedOrganizer() {
      const groupedOrganizer = _.groupBy(this.organizer, "address.city");
      return _.sortBy(
        Object.keys(groupedOrganizer).map(key => ({
          title: key,
          _id: key,
          sub: _.uniqBy(_.sortBy(groupedOrganizer[key], "title"), "_id")
        })),
        "title"
      );
    },
    groupedLocations() {
      const groupedLocations = _.groupBy(this.locations, "address.city");
      return _.sortBy(
        Object.keys(groupedLocations).map(key => ({
          title: key,
          _id: key,
          sub: _.sortBy(groupedLocations[key], "title")
        })),
        "title"
      );
    }
  },
  watch: {
    async selectedPrincipal(newValue, oldValue) {
      if (!newValue?.id || newValue.id === oldValue?.id) {
        return;
      }
      await this.fetchData();
    }
  },
  components: {
    EventListTable,
    EventPreview,
    EventFilter,
    EventEditDialog,
    EventExportPopup
  }
};
